import {
  Flex,
  IconButton,
  Text,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  UnorderedList,
  ListItem,
  Circle,
  Avatar,
  Box,
  Input,
  InputLeftAddon,
  InputGroup,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import NotificationsList from "components/elements/NotificationsList";
import { NotificationsContext } from "providers/notifications";
import { useContext, useState, useEffect } from "react";
import moment from "moment";
import { Link, useLocation } from "react-router-dom";
import UserAvatar from "components/elements/UserAvatar";
import LoanFilters from "components/loans/LoanFilters";
import { apiRequest } from "utils/api";
import LoanSearchResults from "components/elements/SearchResults/LoanSearchResults";
import DocumentSearchResult from "components/elements/SearchResults/DocumentSearchResult";
import BorrowerContactSearch from "components/elements/SearchResults/BorrowerContactSearch";
import BorrowerCompanySearch from "components/elements/SearchResults/BorrowerCompanySearch";
import PropertySearch from "components/elements/SearchResults/PropertySearch";
import { PhoenixDashboardFeesDownload } from "./phoenix-dashboard-fees-download";
import { useCustomizations } from "hooks/customizations/useCustomizations";
import { useLoansQuery } from "modules/loan/queries/use-loans-query";

const PageHeaderIcons = ({ user }: { user: any }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { unread, notifications, dismissNotification } = useContext(NotificationsContext);
  const { data: allLoans = [] } = useLoansQuery();
  const [allDocuments, setAllDocumets] = useState<any[]>([]);
  const [allBorrowerContacts, setAllBorrowerContacts] = useState<any[]>([]);
  const [allBorrowerCompnay, setAllBorrowerCompany] = useState<any[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchResult, setIsSearchResult] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const { isPhoenix } = useCustomizations();

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setIsSearchResult(false);
    setSearchQuery("");
    setIsFocused(false);
  };
  const handleInputFocus = () => {
    setIsSearchResult(true);
    setIsFocused(true);
  };
  const handleInputBlur = () => {
    // setIsSearchResult(false);  // To close the popup
  };

  const getDocument = async () => {
    const { documents } = await apiRequest("/supabase/documents/getAllDocuments");
    setAllDocumets(documents);
  };

  const getBorrowerContacts = async () => {
    const { users }: { users: { first_name: string; last_name: string }[] } = await apiRequest(
      "/supabase/borrowers/select"
    );
    setAllBorrowerContacts(users);
    setAllBorrowerCompany(users);
  };

  const getProperties = async () => {
    const { properties } = await apiRequest("/supabase/properties/select");
    setAllProperties(properties);
  };

  useEffect(() => {
    if (searchQuery !== "") {
      getDocument();
      getBorrowerContacts();
      getProperties();
    }
  }, [searchQuery]);

  const filterLoans = () => {
    return allLoans.filter((loan: any) => {
      const searchLower = searchQuery.toLowerCase();
      const loanName = loan.name.toLowerCase();
      // Extract loan number from UUID (first part before hyphen)
      const loanNumber = loan.id.split("-")[0].toLowerCase();
      return loanName.includes(searchLower) || loanNumber.includes(searchLower);
    });
  };

  const filterDocuments = () => {
    return allDocuments.filter((document: any) => {
      return document.name.toLowerCase().includes(searchQuery.toLowerCase()) ?? null;
    });
  };

  const filterBorrowerContacts = () => {
    return allBorrowerContacts.filter((borrower: any) => {
      return borrower?.first_name?.toLowerCase()?.includes(searchQuery.toLowerCase()) ?? null;
    });
  };

  const filterBorrowerCompany = () => {
    const uniqueCompanies: string[] = [];
    return allBorrowerCompnay.filter((bCompany: any) => {
      const companyName = bCompany?.company?.name?.toLowerCase() ?? null;
      if (companyName) {
        const isUnique = !uniqueCompanies.includes(companyName);
        if (isUnique) {
          uniqueCompanies.push(companyName);
          return companyName.includes(searchQuery.toLowerCase());
        }
      }
      return false;
    });
  };

  const filterProperties = () => {
    return allProperties.filter((property: any) => {
      const searchLower = searchQuery.toLowerCase();
      const propertyName = property.name.toLowerCase();
      const propertyAddress = property.address.toLowerCase();
      return propertyName.includes(searchLower) || propertyAddress.includes(searchLower);
    });
  };

  const navigateToEntity = (entityType: string, id: string) => {
    navigate(`/${entityType}/${id}`);
  };

  const handleEntityClick = (entityType: string, entityId: string) => {
    navigateToEntity(entityType, entityId);
  };

  return (
    <Flex gap={2} alignItems="center">
      {isPhoenix && pathname.includes("/dashboard") && <PhoenixDashboardFeesDownload />}
      {!pathname.includes("/loans/") && !pathname.includes("/properties/") && !isSearchOpen && (
        <Flex gap={2}>
          <LoanFilters />
        </Flex>
      )}

      {/* Search Icon here */}
      <Box>
        {!isSearchOpen && (
          <IconButton
            aria-label="search"
            variant="whiteIcon"
            borderColor={"#E0E0E0"}
            borderWidth={"1px"}
            icon={<FontAwesomeIcon icon={["far", "magnifying-glass"]} />}
            onClick={toggleSearch}
          />
        )}
      </Box>

      {/* parent box */}
      {isSearchOpen && (
        <Box>
          {/* search input box*/}
          <Box display={"column"}>
            <Box w="400px">
              <InputGroup
                py="0px"
                height="48px"
                mr="5px"
                background={"white"}
                color="gray.300"
                fontSize="14px"
                fontWeight="500"
                borderWidth="1.5px"
                borderColor={isFocused ? "black" : "#E0E0E0"}
                borderRadius="25.5px"
              >
                <InputLeftAddon height="32px" pr="0px" pt={"12px"}>
                  <FontAwesomeIcon icon={["far", "magnifying-glass"]} fontSize="14px" />
                </InputLeftAddon>
                <Box p="3px">
                  <Input
                    fontSize="14px"
                    fontWeight="500"
                    pl="8px"
                    py="0px"
                    pt="8px"
                    borderRadius="25.5px"
                    borderColor="#E0E0E0"
                    height="31px"
                    lineHeight="22px"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                  />
                </Box>

                <InputLeftAddon ml={"130px"} mt={"2px"}>
                  <FontAwesomeIcon icon={["far", "xmark"]} fontSize="14px" onClick={toggleSearch} />
                </InputLeftAddon>
              </InputGroup>
            </Box>

            {/* search Result Popup */}
            <Box>
              {isSearchResult && (
                <Box
                  overflow={"auto"}
                  position="absolute"
                  mt={"8px"}
                  w={"400px"}
                  h={"532px"}
                  zIndex={999}
                  bg="white"
                  boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
                  borderRadius="20px"
                  p="20px"
                >
                  <Box borderBottom={"1px solid #E2E2E2"} display={"flex"}>
                    {searchQuery ? <Text> Showing result with </Text> : <Text></Text>}
                    <Text fontWeight={"600"} ml={"5px"}>
                      {" "}
                      {searchQuery}
                    </Text>
                  </Box>

                  {/* For loan search */}
                  <Box>
                    <LoanSearchResults
                      searchQuery={searchQuery}
                      filterLoans={filterLoans}
                      user={user}
                      handleEntityClick={handleEntityClick}
                    />
                    <DocumentSearchResult
                      searchQuery={searchQuery}
                      filterDocuments={filterDocuments}
                      handleEntityClick={handleEntityClick}
                    />
                    <BorrowerContactSearch
                      searchQuery={searchQuery}
                      filterBorrowerContacts={filterBorrowerContacts}
                      handleEntityClick={handleEntityClick}
                    />
                    <BorrowerCompanySearch
                      searchQuery={searchQuery}
                      filterBorrowerCompany={filterBorrowerCompany}
                      handleEntityClick={handleEntityClick}
                    />
                    <PropertySearch
                      searchQuery={searchQuery}
                      filterProperties={filterProperties}
                      handleEntityClick={handleEntityClick}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Messages */}
      {!isSearchOpen && (
        <Box>
          <Popover>
            {({ isOpen }) => (
              <>
                <PopoverTrigger>
                  <IconButton
                    aria-label="messages"
                    variant="whiteIcon"
                    borderColor={isOpen ? "primary" : "#E0E0E0"}
                    borderWidth={isOpen ? "1px" : "1px"}
                    icon={
                      <>
                        <FontAwesomeIcon icon={["fas", "envelope"]} />
                        {unread.length > 0 && (
                          <Circle
                            bg="primary"
                            borderWidth="2px"
                            borderColor="white"
                            size="13px"
                            position="absolute"
                            top="0px"
                            right="0px"
                          ></Circle>
                        )}
                      </>
                    }
                  />
                </PopoverTrigger>
                <PopoverContent
                  minW="420px"
                  border="none"
                  boxShadow="0px 4px 4px rgba(204, 204, 204, 0.25), 0px 0px 15px rgba(198, 198, 198, 0.25)"
                  borderRadius="20px"
                  px="24px"
                  py="10px"
                >
                  {unread.length > 0 && (
                    <PopoverHeader
                      p="0px"
                      borderBottomWidth="0px"
                      fontSize="15px"
                      fontWeight="500"
                      mb="0px"
                    >
                      Messages
                    </PopoverHeader>
                  )}
                  <PopoverBody p="0px">
                    {unread.length > 0 ? (
                      <UnorderedList listStyleType="none" p="0px" m="0px" maxH="400px">
                        {unread.map((notification: any) => (
                          <ListItem
                            key={notification.id}
                            borderBottomWidth="1px"
                            borderBottomColor="gray.50"
                            _last={{
                              borderBottomWidth: "0px",
                              pb: "0px",
                            }}
                          >
                            <Link to={`/messages/${notification.conversation}`}>
                              <Flex py="10px">
                                <Avatar
                                  w="40px"
                                  h="40px"
                                  mr="15px"
                                  name={`${notification.event.message.from.first_name} ${notification.event.message.from.last_name}`}
                                />
                                <Flex flexDir="column" flexGrow="1">
                                  <Text fontSize="13px" fontWeight="500" lineHeight="20px">
                                    {notification.event.message.from.first_name}{" "}
                                    {notification.event.message.from.last_name}
                                  </Text>
                                  <Flex
                                    flexGrow="1"
                                    justifyContent="space-between"
                                    color="gray.200"
                                    fontSize="12px"
                                    lineHeight="20px"
                                    fontWeight="500"
                                  >
                                    <Text noOfLines={2}>
                                      {moment(notification.created_at).fromNow()} &middot;{" "}
                                      {notification.event.message.body}
                                    </Text>
                                  </Flex>
                                </Flex>
                              </Flex>
                            </Link>
                          </ListItem>
                        ))}
                      </UnorderedList>
                    ) : (
                      <Text
                        w="100%"
                        textAlign="center"
                        color="gray.200"
                        fontSize="12px"
                        lineHeight="40px"
                        fontWeight="500"
                      >
                        No new messages!
                      </Text>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        </Box>
      )}

      {/* Notifications */}
      {!isSearchOpen && (
        <Box>
          <Popover>
            {({ isOpen }) => (
              <>
                <PopoverTrigger>
                  <IconButton
                    aria-label="notifications"
                    variant="whiteIcon"
                    borderColor={isOpen ? "primary" : "#E0E0E0"}
                    borderWidth={isOpen ? "1px" : "1px"}
                    icon={
                      <>
                        <FontAwesomeIcon icon={["fas", "bell"]} />
                        {notifications.length > 0 && (
                          <Circle
                            bg="primary"
                            borderWidth="2px"
                            borderColor="white"
                            size="13px"
                            position="absolute"
                            top="0px"
                            right="0px"
                          ></Circle>
                        )}
                      </>
                    }
                  />
                </PopoverTrigger>
                <PopoverContent
                  bg="white"
                  border="none"
                  borderRadius="12px"
                  boxShadow="0px 2px 8px 0px #00000014"
                >
                  {notifications.length > 0 && (
                    <PopoverHeader fontSize="15px" fontWeight="500" borderColor="gray.50">
                      Notifications
                    </PopoverHeader>
                  )}
                  <PopoverBody>
                    {notifications.length > 0 ? (
                      <NotificationsList
                        notifications={notifications}
                        dismissNotification={dismissNotification}
                      />
                    ) : (
                      <Text
                        w="100%"
                        textAlign="center"
                        color="gray.200"
                        fontSize="12px"
                        lineHeight="40px"
                        fontWeight="500"
                      >
                        No new notifications!
                      </Text>
                    )}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        </Box>
      )}

      <Link to="/settings/account">
        <Flex alignItems="center">
          {user.avatar ? (
            <Box
              borderRadius="50px"
              boxShadow="0px 4px 4px rgba(204, 204, 204, 0.25), 0px 0px 15px rgba(198, 198, 198, 0.25)"
              borderWidth="2px"
              borderColor="white"
            >
              <UserAvatar user={user} size="47px" hideOnline />
            </Box>
          ) : (
            <IconButton
              aria-label="search"
              variant="whiteIcon"
              border={"1px solid #E0E0E0"}
              icon={<FontAwesomeIcon icon={["far", "user"]} />}
            />
          )}
          <Text fontWeight="600" fontSize="20px" ml="7px" mr="10px">
            {`${user.first_name} ${user.last_name}`}
          </Text>
        </Flex>
      </Link>
    </Flex>
  );
};

export default PageHeaderIcons;
